import React, { useEffect, useState } from 'react';
import { Card, Table, Badge, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { FaExclamationTriangle, FaBell, FaTachometerAlt, FaClock, FaEye } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../../styles/Flota.css';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4004').replace(/\/$/, '');

const FlotaAlertas = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    cargarAlertas();
  }, []);

  const cargarAlertas = async () => {
    try {
      setLoading(true);
      setError(null);

      const authToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : null;

      if (!authToken || !user) {
        setError('No hay sesión activa');
        return;
      }

      const userId = user.Legajo || user.CdEmpleado || user.CdUsuario;
      const userRole = user.IdRol || user.tipoUsuario || 1;

      const response = await axios.get(`${API_URL}/api/flota/alertas`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
          'x-user-id': String(userId),
          'x-user-role': String(userRole)
        }
      });

      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError('No se pudieron cargar las alertas');
      }
    } catch (err) {
      console.error('Error al cargar alertas:', err);
      setError(err.response?.data?.error || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'Nunca';
    return new Date(fecha).toLocaleDateString('es-AR');
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('es-AR').format(Math.round(num));
  };

  const verDetalle = (interno) => {
    navigate(`/administrador/flota/unidades/${interno}`);
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Cargando alertas operativas...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error}</p>
      </Alert>
    );
  }

  if (!data) return null;

  const totalAlertas = 
    data.sinActividad.total + 
    data.conNovedades.total + 
    data.kmsBajos.total + 
    data.kmsAltos.total;

  return (
    <div className="flota-alertas">
      <div className="page-header mb-4">
        <h2><FaBell className="me-2" />Alertas Operativas</h2>
        <p className="text-muted">Monitoreo de desvíos y situaciones que requieren atención</p>
      </div>

      {/* Resumen de alertas */}
      <Row className="mb-4">
        <Col md={3} sm={6} className="mb-3">
          <Card className="alert-card border-warning">
            <Card.Body>
              <div className="alert-icon bg-warning">
                <FaClock />
              </div>
              <div className="alert-content">
                <h3>{data.sinActividad.total}</h3>
                <p>Sin Actividad +7 días</p>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className="mb-3">
          <Card className="alert-card border-danger">
            <Card.Body>
              <div className="alert-icon bg-danger">
                <FaExclamationTriangle />
              </div>
              <div className="alert-content">
                <h3>{data.conNovedades.total}</h3>
                <p>Con Novedades</p>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className="mb-3">
          <Card className="alert-card border-info">
            <Card.Body>
              <div className="alert-icon bg-info">
                <FaTachometerAlt />
              </div>
              <div className="alert-content">
                <h3>{data.kmsBajos.total}</h3>
                <p>Km Muy Bajos</p>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className="mb-3">
          <Card className="alert-card border-warning">
            <Card.Body>
              <div className="alert-icon bg-warning">
                <FaTachometerAlt />
              </div>
              <div className="alert-content">
                <h3>{data.kmsAltos.total}</h3>
                <p>Km Muy Altos</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {totalAlertas === 0 && (
        <Alert variant="success">
          <FaExclamationTriangle className="me-2" />
          <strong>¡Excelente!</strong> No hay alertas activas en este momento.
        </Alert>
      )}

      {/* Unidades sin actividad */}
      {data.sinActividad.total > 0 && (
        <Card className="mb-4">
          <Card.Header className="bg-warning">
            <FaClock className="me-2" />
            <strong>Unidades Sin Actividad (+7 días)</strong>
            <Badge bg="dark" className="ms-2">{data.sinActividad.total}</Badge>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Interno</th>
                    <th>Dominio</th>
                    <th>Estado</th>
                    <th>Novedad</th>
                    <th>Última Actividad</th>
                    <th className="text-center">Días Sin Actividad</th>
                    <th>Observaciones</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sinActividad.items.map((item) => (
                    <tr key={item.Interno}>
                      <td><strong>{item.Interno}</strong></td>
                      <td>{item.Dominio || '-'}</td>
                      <td>
                        <Badge 
                          bg="secondary"
                          style={{ backgroundColor: item.ColorEstado || '#6c757d' }}
                        >
                          {item.NmEstado}
                        </Badge>
                      </td>
                      <td>
                        {item.NmNovedad ? (
                          <Badge bg="danger">{item.NmNovedad}</Badge>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>{formatFecha(item.UltimaActividad)}</td>
                      <td className="text-center">
                        <Badge 
                          bg={item.DiasSinActividad > 30 ? 'danger' : item.DiasSinActividad > 15 ? 'warning' : 'secondary'}
                          style={{ fontSize: '0.95rem' }}
                        >
                          {item.DiasSinActividad >= 999 ? 'Nunca' : item.DiasSinActividad}
                        </Badge>
                      </td>
                      <td style={{ fontSize: '0.85rem' }}>{item.Obs || '-'}</td>
                      <td className="text-center">
                        <Badge 
                          bg="primary" 
                          style={{ cursor: 'pointer' }}
                          onClick={() => verDetalle(item.Interno)}
                        >
                          <FaEye /> Ver
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Unidades con novedades */}
      {data.conNovedades.total > 0 && (
        <Card className="mb-4">
          <Card.Header className="bg-danger text-white">
            <FaExclamationTriangle className="me-2" />
            <strong>Unidades con Novedades Activas</strong>
            <Badge bg="dark" className="ms-2">{data.conNovedades.total}</Badge>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Interno</th>
                    <th>Dominio</th>
                    <th>Estado</th>
                    <th>Novedad</th>
                    <th>Observaciones</th>
                    <th>Última Actualización</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {data.conNovedades.items.map((item) => (
                    <tr key={item.Interno}>
                      <td><strong>{item.Interno}</strong></td>
                      <td>{item.Dominio || '-'}</td>
                      <td>
                        <Badge bg="secondary">{item.NmEstado}</Badge>
                      </td>
                      <td>
                        <Badge bg="danger">{item.NmNovedad}</Badge>
                      </td>
                      <td style={{ fontSize: '0.85rem' }}>{item.Obs || '-'}</td>
                      <td>{formatFecha(item.FecUltAct)}</td>
                      <td className="text-center">
                        <Badge 
                          bg="primary" 
                          style={{ cursor: 'pointer' }}
                          onClick={() => verDetalle(item.Interno)}
                        >
                          <FaEye /> Ver
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Kilómetros muy bajos */}
      {data.kmsBajos.total > 0 && (
        <Card className="mb-4">
          <Card.Header className="bg-info text-white">
            <FaTachometerAlt className="me-2" />
            <strong>Unidades con Kilómetros Bajos (&lt;50 km promedio/día)</strong>
            <Badge bg="dark" className="ms-2">{data.kmsBajos.total}</Badge>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Interno</th>
                    <th>Dominio</th>
                    <th>Estado</th>
                    <th className="text-center">Días Activos</th>
                    <th className="text-end">Total Km (30d)</th>
                    <th className="text-end">Promedio Km/día</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {data.kmsBajos.items.map((item) => (
                    <tr key={item.Interno}>
                      <td><strong>{item.Interno}</strong></td>
                      <td>{item.Dominio || '-'}</td>
                      <td>
                        <Badge bg="secondary">{item.NmEstado}</Badge>
                      </td>
                      <td className="text-center">
                        <Badge bg="primary">{item.DiasActivos}</Badge>
                      </td>
                      <td className="text-end">{formatNumber(item.TotalKms)} km</td>
                      <td className="text-end">
                        <Badge bg="warning">{formatNumber(item.PromedioKmsPorDia)} km</Badge>
                      </td>
                      <td className="text-center">
                        <Badge 
                          bg="primary" 
                          style={{ cursor: 'pointer' }}
                          onClick={() => verDetalle(item.Interno)}
                        >
                          <FaEye /> Ver
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Kilómetros muy altos */}
      {data.kmsAltos.total > 0 && (
        <Card className="mb-4">
          <Card.Header className="bg-warning">
            <FaTachometerAlt className="me-2" />
            <strong>Unidades con Kilómetros Altos (&gt;300 km promedio/día)</strong>
            <Badge bg="dark" className="ms-2">{data.kmsAltos.total}</Badge>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Interno</th>
                    <th>Dominio</th>
                    <th>Estado</th>
                    <th className="text-center">Días Activos</th>
                    <th className="text-end">Total Km (30d)</th>
                    <th className="text-end">Promedio Km/día</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {data.kmsAltos.items.map((item) => (
                    <tr key={item.Interno}>
                      <td><strong>{item.Interno}</strong></td>
                      <td>{item.Dominio || '-'}</td>
                      <td>
                        <Badge bg="secondary">{item.NmEstado}</Badge>
                      </td>
                      <td className="text-center">
                        <Badge bg="primary">{item.DiasActivos}</Badge>
                      </td>
                      <td className="text-end">{formatNumber(item.TotalKms)} km</td>
                      <td className="text-end">
                        <Badge bg="danger">{formatNumber(item.PromedioKmsPorDia)} km</Badge>
                      </td>
                      <td className="text-center">
                        <Badge 
                          bg="primary" 
                          style={{ cursor: 'pointer' }}
                          onClick={() => verDetalle(item.Interno)}
                        >
                          <FaEye /> Ver
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default FlotaAlertas;
